import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../store/hooks';
import { apiService } from '../../services/apiService';
import { FaSpinner, FaExclamationTriangle, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import './OrderSubmissionForm.css';

interface OrderSubmissionFormProps {
  symbol?: string;
  onOrderSubmitted?: () => void;
  onClose?: () => void;
}

interface OrderSubmissionRequest {
  symbol: string;
  side: 'BUY' | 'SELL';
  type: 'MARKET' | 'LIMIT';
  quantity: number;
  price?: number;
  timeInForce?: 'GTC' | 'IOC' | 'FOK';
  stopPrice?: number;
}

interface MockDataStatus {
  mockDataEnabled: boolean;
  message: string;
}

/**
 * Order Submission Form Component
 * Allows users to submit buy/sell orders to the matching engine
 */
const OrderSubmissionForm: React.FC<OrderSubmissionFormProps> = ({
  symbol: initialSymbol = 'BTC-USDT',
  onOrderSubmitted,
  onClose
}) => {
  const [symbol, setSymbol] = useState(initialSymbol);
  const [side, setSide] = useState<'BUY' | 'SELL'>('BUY');
  const [type, setType] = useState<'MARKET' | 'LIMIT'>('LIMIT');
  const [quantity, setQuantity] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [timeInForce, setTimeInForce] = useState<'GTC' | 'IOC' | 'FOK'>('GTC');
  const [stopPrice, setStopPrice] = useState<string>('');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [mockDataStatus, setMockDataStatus] = useState<MockDataStatus | null>(null);
  const [showMockToggle, setShowMockToggle] = useState(false);
  
  const token = useAppSelector(state => state.auth.token);
  const profile = useAppSelector(state => state.profile.profile);
  
  const supportedSymbols = ['BTC-USDT', 'ETH-USDT', 'DOGE-USDT', 'SHIB-USDT', 'PEPE-USDT', 'FLOKI-USDT', 'BONK-USDT'];
  
  // Load mock data status
  useEffect(() => {
    loadMockDataStatus();
  }, []);
  
  const loadMockDataStatus = async () => {
    try {
      const response = await apiService.get<MockDataStatus>('/api/retail/matching-engine/mock-data/status');
      if (response.success && response.data) {
        const apiResponse = response.data as any;
        if (apiResponse.status === 'Successful' && apiResponse.data) {
          setMockDataStatus(apiResponse.data);
        } else if (apiResponse.mockDataEnabled !== undefined) {
          setMockDataStatus(apiResponse);
        }
      }
    } catch (err) {
      console.error('Error loading mock data status:', err);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      const orderRequest: OrderSubmissionRequest = {
        symbol,
        side,
        type,
        quantity: parseFloat(quantity),
        timeInForce
      };
      
      if (type === 'LIMIT' && price) {
        orderRequest.price = parseFloat(price);
      }
      
      if (stopPrice) {
        orderRequest.stopPrice = parseFloat(stopPrice);
      }
      
      const response = await apiService.post('/api/retail/matching-engine/orders', orderRequest);
      
      if (response.success) {
        setSuccess('Order submitted successfully!');
        // Reset form
        setQuantity('');
        setPrice('');
        setStopPrice('');
        
        if (onOrderSubmitted) {
          onOrderSubmitted();
        }
        
        // Auto-close after 2 seconds
        setTimeout(() => {
          if (onClose) {
            onClose();
          }
        }, 2000);
      } else {
        setError(response.error || 'Failed to submit order');
      }
    } catch (err: any) {
      console.error('Error submitting order:', err);
      setError(err.message || err.response?.data?.message || 'Failed to submit order');
    } finally {
      setLoading(false);
    }
  };
  
  const calculateEstimatedCost = () => {
    if (!quantity || quantity === '0') return null;
    
    const qty = parseFloat(quantity);
    const prc = type === 'MARKET' ? null : (price ? parseFloat(price) : null);
    
    if (side === 'BUY') {
      if (prc) {
        const total = qty * prc;
        const fee = total * 0.002; // Taker fee estimate
        return { total: total + fee, fee };
      }
      return { total: null, fee: null };
    } else {
      if (prc) {
        const total = qty * prc;
        const fee = total * 0.002; // Taker fee estimate
        return { total: total - fee, fee };
      }
      return { total: null, fee: null };
    }
  };
  
  const estimatedCost = calculateEstimatedCost();
  
  // Try to get permissions from multiple sources
  const authPermissions = useAppSelector((state: any) => state?.auth?.permissions || []);
  const adminPermissions = useAppSelector((state: any) => state?.auth?.adminUser?.permissions || []);
  const profilePermissions = useAppSelector((state: any) => state?.profile?.profile?.permissions || []);
  
  // Combine all permission sources
  const allPermissions = [
    ...authPermissions,
    ...(Array.isArray(adminPermissions) ? adminPermissions : []),
    ...(Array.isArray(profilePermissions) ? profilePermissions : [])
  ];
  
  const hasOrderPermission = allPermissions.includes('SUBMIT_TRADING_ORDERS') || 
                             allPermissions.some((p: any) => p === 'SUBMIT_TRADING_ORDERS' || 
                                                              (typeof p === 'string' && p.includes('SUBMIT_TRADING_ORDERS')));
  
  if (!token) {
    return (
      <div className="order-submission-form no-permission">
        <FaExclamationTriangle className="text-red-500 text-4xl mb-4" />
        <p className="text-lg text-gray-600">Please log in to submit orders.</p>
      </div>
    );
  }
  
  if (!hasOrderPermission) {
    return (
      <div className="order-submission-form no-permission">
        <FaExclamationTriangle className="text-red-500 text-4xl mb-4" />
        <p className="text-lg text-gray-600">You do not have permission to submit orders.</p>
      </div>
    );
  }
  
  return (
    <div className="order-submission-form bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-semibold text-gray-800">Place Order</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        )}
      </div>
      
      {/* Mock Data Indicator */}
      {mockDataStatus && (
        <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 ${
          mockDataStatus.mockDataEnabled 
            ? 'bg-yellow-50 border border-yellow-200' 
            : 'bg-green-50 border border-green-200'
        }`}>
          <FaInfoCircle className={mockDataStatus.mockDataEnabled ? 'text-yellow-600' : 'text-green-600'} />
          <span className={`text-sm font-medium ${
            mockDataStatus.mockDataEnabled ? 'text-yellow-800' : 'text-green-800'
          }`}>
            {mockDataStatus.message}
          </span>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Symbol Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Symbol</label>
          <select
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-accent-blue"
            required
          >
            {supportedSymbols.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        
        {/* Side Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Side</label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setSide('BUY')}
              className={`flex-1 px-4 py-2.5 rounded-lg font-mukta font-medium text-sm transition-colors ${
                side === 'BUY'
                  ? 'bg-zazoo-green text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Buy
            </button>
            <button
              type="button"
              onClick={() => setSide('SELL')}
              className={`flex-1 px-4 py-2.5 rounded-lg font-mukta font-medium text-sm transition-colors ${
                side === 'SELL'
                  ? 'bg-zazoo-red text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Sell
            </button>
          </div>
        </div>
        
        {/* Order Type */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Order Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as 'MARKET' | 'LIMIT')}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-accent-blue"
            required
          >
            <option value="MARKET">Market</option>
            <option value="LIMIT">Limit</option>
          </select>
        </div>
        
        {/* Quantity */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity</label>
          <input
            type="number"
            step="0.0001"
            min="0.0001"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-accent-blue"
            placeholder="0.0000"
            required
          />
        </div>
        
        {/* Price (for Limit orders) */}
        {type === 'LIMIT' && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Price</label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-accent-blue"
              placeholder="0.00"
              required
            />
          </div>
        )}
        
        {/* Time in Force */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Time in Force</label>
          <select
            value={timeInForce}
            onChange={(e) => setTimeInForce(e.target.value as 'GTC' | 'IOC' | 'FOK')}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg font-mukta text-sm focus:outline-none focus:ring-2 focus:ring-zazoo-accent-blue"
          >
            <option value="GTC">Good Till Cancel (GTC)</option>
            <option value="IOC">Immediate Or Cancel (IOC)</option>
            <option value="FOK">Fill Or Kill (FOK)</option>
          </select>
        </div>
        
        {/* Estimated Cost */}
        {estimatedCost && estimatedCost.total !== null && (
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Estimated {side === 'BUY' ? 'Cost' : 'Proceeds'}:</span>
              <span className="font-semibold text-gray-800">
                ${estimatedCost.total.toFixed(2)}
              </span>
            </div>
            {estimatedCost.fee !== null && (
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Estimated Fee:</span>
                <span>${estimatedCost.fee.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="flex items-start gap-3 p-4 bg-zazoo-red/10 border border-zazoo-red rounded-lg">
            <FaExclamationTriangle className="text-zazoo-red flex-shrink-0 mt-0.5" />
            <p className="text-sm text-zazoo-red">{error}</p>
          </div>
        )}
        
        {/* Success Message */}
        {success && (
          <div className="flex items-start gap-3 p-4 bg-zazoo-green/10 border border-zazoo-green rounded-lg">
            <FaCheckCircle className="text-zazoo-green flex-shrink-0 mt-0.5" />
            <p className="text-sm text-zazoo-green">{success}</p>
          </div>
        )}
        
        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`w-full px-6 py-2.5 rounded-full font-mukta font-medium text-sm text-white transition-opacity ${
            side === 'BUY'
              ? 'bg-zazoo-green hover:opacity-90'
              : 'bg-zazoo-red hover:opacity-90'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin inline-block mr-2" />
              Submitting...
            </>
          ) : (
            `${side} ${symbol.split('-')[0]}`
          )}
        </button>
      </form>
    </div>
  );
};

export default OrderSubmissionForm;

